import React from 'react';
import { Building2, UserCheck, Building, Shield, User, ChevronRight, LogOut } from 'lucide-react';

export interface RoleOption {
  id: string; // 'team' | 'referrer' | 'employer' | 'sponsor' | 'applicant'
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string;
}

/**
 * Raw role detections for a signed-in user, before they are turned into
 * role-option flags. `detectedTeamRole` is the raw string from the
 * workspace team-role heuristic ('' when none); `isAdminClaim` is true when
 * the Firebase token carries the admin claim or the profile is a
 * platform/admin account (server-issued truth).
 */
export interface DetectedUserRoles {
  isAdminClaim: boolean;
  detectedTeamRole: string;
  detectedEmployer: boolean;
  detectedReferrer: boolean;
  detectedSponsor: boolean;
  hasApplicant: boolean;
}

/**
 * Compute the role-option flags for {@link getRoleOptionsForUser} from raw
 * detections.
 *
 * Org-console accounts (referrer/employer/sponsor) are NEVER workspace team
 * members: on org approval, the server stamps
 * `workspaceMembers[workspaceId] = 'admin'` for org super-agents ("org
 * owner"), which a naive team-role check misreads as team membership and
 * would offer "Workspace Team Member" to referrers/employers. The team option
 * is therefore offered only on a genuine team signal: the admin custom claim
 * (or platform-admin profile), or a detected team role held by a user who
 * holds no org role.
 */
export function computeRoleOptionFlags(d: DetectedUserRoles): {
  hasTeam: boolean;
  hasEmployer: boolean;
  hasReferrer: boolean;
  hasSponsor: boolean;
  hasApplicant: boolean;
} {
  const hasOrgRole = d.detectedEmployer || d.detectedReferrer || d.detectedSponsor;
  return {
    // Org-console accounts (referrer/employer/sponsor) NEVER get the team
    // option — not even when a stale `admin` custom claim is present. The
    // org-owner `workspaceMembers[ws]='admin'` stamp means "org owner", never
    // team membership. (Fixes the intermittent 3-card render where
    // `isAdminClaim` bypassed the `!hasOrgRole` guard.)
    hasTeam: Boolean(!hasOrgRole && (d.isAdminClaim || d.detectedTeamRole)),
    hasEmployer: d.detectedEmployer,
    hasReferrer: d.detectedReferrer,
    hasSponsor: d.detectedSponsor,
    hasApplicant: d.hasApplicant,
  };
}

interface RoleSelectionScreenProps {
  availableRoles: RoleOption[];
  onSelectRole: (roleId: string) => void;
  onLogout: () => void;
  displayName?: string;
  email?: string;
}

export function getRoleOptionsForUser(flags: {
  hasTeam: boolean;
  hasReferrer: boolean;
  hasEmployer: boolean;
  hasSponsor: boolean;
  hasApplicant: boolean;
  teamRoleLabel?: string;
}): RoleOption[] {
  const options: RoleOption[] = [];

  if (flags.hasTeam) {
    options.push({
      id: 'team',
      title: 'Workspace Team Member',
      description: 'Access candidate management, workspace administration, and operational tools.',
      icon: Building2,
      badge: flags.teamRoleLabel || 'Team Staff',
    });
  }

  if (flags.hasReferrer) {
    options.push({
      id: 'referrer',
      title: 'Referrer Dashboard',
      description: 'Refer candidates, track your referrals, and see your commissions.',
      icon: UserCheck,
      badge: 'Referrer',
    });
  }

  if (flags.hasEmployer) {
    options.push({
      id: 'employer',
      title: 'Employer Dashboard',
      description: 'Post job openings, review candidate applications, and schedule interview slots.',
      icon: Building,
      badge: 'Employer',
    });
  }

  if (flags.hasSponsor) {
    options.push({
      id: 'sponsor',
      title: 'Sponsor Dashboard',
      description: 'Review exchange cases, verify documents, and manage compliance.',
      icon: Shield,
      badge: 'Sponsor',
    });
  }

  if (flags.hasApplicant) {
    options.push({
      id: 'applicant',
      title: 'My Application',
      description: 'Work on your own application and browse exchange opportunities.',
      icon: User,
      badge: 'Applicant',
    });
  }

  // If user has no assigned roles at all (new user needing mandatory role selection)
  if (options.length === 0) {
    return [
      {
        id: 'applicant',
        title: 'My Application',
        description: 'Work on your own application and browse exchange opportunities.',
        icon: User,
        badge: 'Applicant',
      },
      {
        id: 'referrer',
        title: 'Referrer Dashboard',
        description: 'Refer candidates, track your referrals, and see your commissions.',
        icon: UserCheck,
        badge: 'Referrer',
      },
      {
        id: 'employer',
        title: 'Employer Dashboard',
        description: 'Hiring candidates for your organization',
        icon: Building,
        badge: 'Employer',
      },
      {
        id: 'sponsor',
        title: 'Sponsor Dashboard',
        description: 'Sponsoring exchange visitors',
        icon: Shield,
        badge: 'Sponsor',
      },
    ];
  }

  return options;
}

export default function RoleSelectionScreen({
  availableRoles,
  onSelectRole,
  onLogout,
  displayName,
  email,
}: RoleSelectionScreenProps) {
  return (
    <div className="min-h-dvh flex flex-col items-center justify-center bg-slate-100 p-4 sm:p-6 font-sans text-slate-800">
      <div className="w-full max-w-lg bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 shadow-xl space-y-6 animate-fade-in">
        <div className="text-center space-y-2">
          <div className="mx-auto w-12 h-12 rounded-2xl btn-primary flex items-center justify-center text-white shadow-lg shadow-blue-500/20">
            <Building2 className="h-6 w-6" />
          </div>
          <h2 className="text-xl sm:text-2xl font-black font-display text-slate-900 tracking-tight">
            Select Your Role
          </h2>
          <p className="text-xs text-slate-500 max-w-sm mx-auto leading-relaxed">
            Welcome{displayName ? `, ${displayName}` : ''}! You have more than one role on this account. Choose where you'd like to go.
          </p>
          {email && (
            <p className="text-[11px] font-mono text-slate-500 truncate">{email}</p>
          )}
        </div>

        <div className="space-y-3">
          {availableRoles.map((role) => {
            const Icon = role.icon;
            return (
              <button
                key={role.id}
                onClick={() => onSelectRole(role.id)}
                className="w-full text-left p-4 rounded-2xl bg-white hover:bg-blue-50 border border-slate-200 hover:border-blue-500 shadow-sm transition-all cursor-pointer group flex items-start gap-4"
              >
                <div className="p-2.5 rounded-xl bg-blue-50 text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors shrink-0 mt-0.5">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-sm font-bold text-slate-800 group-hover:text-blue-700 transition-colors">
                      {role.title}
                    </h3>
                    {role.badge && (
                      <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-600 border border-indigo-100 shrink-0">
                        {role.badge}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1 leading-snug line-clamp-2">
                    {role.description}
                  </p>
                </div>
                <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-blue-600 group-hover:translate-x-0.5 transition-all self-center shrink-0" />
              </button>
            );
          })}
        </div>

        <div className="pt-4 border-t border-slate-200 flex items-center justify-between text-xs">
          <span className="text-slate-500">You can switch roles later from your profile menu.</span>
          <button
            onClick={onLogout}
            className="flex items-center gap-1.5 text-rose-600 hover:text-rose-500 font-bold transition cursor-pointer"
          >
            <LogOut className="h-3.5 w-3.5" />
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * The mandatory role-selection options for a referrer WITHOUT Enhanced
 * Referrer Access. Exactly two options — "My Application" and
 * "Referrer Dashboard" —
 * never "Workspace Team Member" (restricted to genuine team members per
 * #1074). The selection is mandatory on every login; the user picks which
 * context to enter and can switch between the two in-app.
 */
export function getNonEnhancedReferrerOptions(): RoleOption[] {
  return [
    {
      id: 'applicant',
      title: 'My Application',
      description: 'Work on your own application and browse exchange opportunities.',
      icon: User,
      badge: 'Applicant',
    },
    {
      id: 'referrer',
      title: 'Referrer Dashboard',
      description: 'Refer candidates, track your referrals, and see your commissions.',
      icon: UserCheck,
      badge: 'Referrer',
    },
  ];
}
